import * as THREE from 'three';
import type { Appearance, Gender, SceneData, NpcInfo, LocationInfo, DanceKey } from '@dn/shared';
import { Engine, disposeTree } from '../core/Engine';
import { ThirdPersonCamera } from '../camera/ThirdPersonCamera';
import { InputController } from '../controls/InputController';
import { CharacterModel } from '../characters/CharacterModel';
import { PlayerController } from '../characters/PlayerController';
import { circle, resolveCircle } from '../physics/Collision';
import type { BuiltScene, SceneBuilder } from './SceneTypes';
import { buildGateScene } from './scenes/GateScene';
import { buildMirrorHallScene } from './scenes/MirrorHallScene';
import { buildThemedScene, THEMED_SCENE_KEYS } from './scenes/ThemedScene';
import { buildFestivalScene } from './scenes/FestivalScene';
import { buildVillaScene } from './scenes/VillaScene';
import { buildVongXuyenScene } from './scenes/VongXuyenScene';
import { enhance, animateWater, lightPools } from '../effects/Realism';
import { buildHeavenScene } from './scenes/HeavenScene';
import { buildVillageScene } from './scenes/VillageScene';
import { nameSprite } from '../effects/textures';
import { glowTexture } from '../effects/Particles';
import { RemotePlayers } from '../characters/RemotePlayers';
import { LampBearer } from '../characters/LampBearer';
import { RiverLanterns, LostSoul, Incense } from '../effects/Rituals';
import { LionCub } from '../characters/LionCub';
import { Fireworks, SkyLanterns, BuffAura, LotusLights } from '../effects/Fireworks';
import { CourtSet, buildConferenceHall, type YamaJudgmentInfo, type YamaKingInfo } from './Yama';

export const SCENES: Record<string, SceneBuilder> = { gate: buildGateScene, mirror_hall: buildMirrorHallScene, festival: buildFestivalScene, village: buildVillageScene, villa: buildVillaScene, vongxuyen: buildVongXuyenScene, heaven: buildHeavenScene };
for (const k of THEMED_SCENE_KEYS) SCENES[k] = buildThemedScene(k);
export const isSceneBuilt = (key: string) => key in SCENES;

export type Interactable =
  | { kind: 'npc'; id: number; key: string; name: string; pos: THREE.Vector3; range: number }
  | { kind: 'location'; key: string; name: string; pos: THREE.Vector3; range: number; loc: LocationInfo }
  | { kind: 'collectible'; key: string; name: string; pos: THREE.Vector3; range: number }
  | { kind: 'yama'; key: string; name: string; pos: THREE.Vector3; range: number; action: 'king' | 'desk' | 'evidence'; king: string }
  | { kind: 'companion'; key: 'lao_dang'; name: string; pos: THREE.Vector3; range: number }
  | { kind: 'soul'; key: 'linh_hon'; name: string; pos: THREE.Vector3; range: number }
  | { kind: 'venue'; key: string; name: string; pos: THREE.Vector3; range: number }
  | { kind: 'mascot'; key: 'be_lan'; name: string; pos: THREE.Vector3; range: number };

const NPC_LOOK: Record<string, { appearance: Appearance; gender: Gender; variant: NonNullable<import('../characters/CharacterModel').CharacterLook['variant']> }> = {
  dia_tang: { appearance: { hairStyle: 'ngan', hairColor: '#e2b890', face: 'hien', skinTone: '#e2b890', outfit: 'ca_sa_vang', accessory: 'khong', aura: 'dom_vang' }, gender: 'nam', variant: 'dia_tang' },
  muc_lien: { appearance: { hairStyle: 'ngan', hairColor: '#c8966a', face: 'hien', skinTone: '#c8966a', outfit: 'ca_sa_nau', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'muc_lien' },
  thach_sanh: { appearance: { hairStyle: 'ngan', hairColor: '#1b1411', face: 'nghiem', skinTone: '#b98a5e', outfit: 'ao_nau', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'thach_sanh' },
  co_tam: { appearance: { hairStyle: 'xoa_dai', hairColor: '#1b1411', face: 'hien', skinTone: '#ecc8a8', outfit: 'ao_tu_than', accessory: 'khong', aura: 'khong' }, gender: 'nu', variant: 'co_tam' },
  cuoi: { appearance: { hairStyle: 'ngan', hairColor: '#1b1411', face: 'hien', skinTone: '#c0905e', outfit: 'ao_nau', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'cuoi' },
  envoy: { appearance: { hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'nghiem', skinTone: '#d8c3b0', outfit: 'ao_lam', accessory: 'khong', aura: 'suong_lam' }, gender: 'nam', variant: 'envoy' },
  thien_su: { appearance: { hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'hien', skinTone: '#f0d4b8', outfit: 'ao_trang', accessory: 'tram_cai', aura: 'dom_vang' }, gender: 'nu', variant: 'co_tam' },
  ngoc_hoang: { appearance: { hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'nghiem', skinTone: '#e8c8a0', outfit: 'long_bao', accessory: 'khong', aura: 'dom_vang' }, gender: 'nam', variant: 'emperor' },
  nam_tao: { appearance: { hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'hien', skinTone: '#e0b890', outfit: 'ao_hoa_dang', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'scribe' },
  bac_dau: { appearance: { hairStyle: 'buoi_cao', hairColor: '#d8d2cc', face: 'nghiem', skinTone: '#d8b088', outfit: 'ao_lam', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'scribe' },
  lao_quan: { appearance: { hairStyle: 'buoi_cao', hairColor: '#f4f0ea', face: 'hien', skinTone: '#e8c8a8', outfit: 'ao_trang', accessory: 'khong', aura: 'suong_lam' }, gender: 'nam', variant: 'sage' },
  thien_tuong: { appearance: { hairStyle: 'ngan', hairColor: '#1b1411', face: 'nghiem', skinTone: '#c8946a', outfit: 'giap_vang', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'heaven_guard' },
  tien_nu: { appearance: { hairStyle: 'xoa_dai', hairColor: '#1b1411', face: 'hien', skinTone: '#f0d4b8', outfit: 'ao_sen', accessory: 'khong', aura: 'dom_vang' }, gender: 'nu', variant: 'fairy' },
  manh_ba: { appearance: { hairStyle: 'buoi_cao', hairColor: '#e8e4dc', face: 'hien', skinTone: '#d8b090', outfit: 'ao_nau', accessory: 'khong', aura: 'suong_lam' }, gender: 'nu', variant: 'vendor' },
  guide: { appearance: { hairStyle: 'ngan', hairColor: '#9a9089', face: 'hien', skinTone: '#b9875f', outfit: 'ao_nau', accessory: 'chuoi_hat', aura: 'khong' }, gender: 'nam', variant: 'guide' },
  gatekeeper: { appearance: { hairStyle: 'khan_vay', hairColor: '#1b1411', face: 'nghiem', skinTone: '#8a5d3d', outfit: 'ao_tram', accessory: 'khong', aura: 'khong' }, gender: 'nam', variant: 'gatekeeper' },
  mirror_keeper: { appearance: { hairStyle: 'xoa_dai', hairColor: '#9a9089', face: 'hien', skinTone: '#f1d2b4', outfit: 'ao_trang', accessory: 'tram_cai', aura: 'dom_vang' }, gender: 'nu', variant: 'mirror_keeper' },
  monk: { appearance: { hairStyle: 'ngan', hairColor: '#2a2420', face: 'hien', skinTone: '#c99a72', outfit: 'ao_nau', accessory: 'chuoi_hat', aura: 'dom_vang' }, gender: 'nam', variant: 'guide' },
  elder: { appearance: { hairStyle: 'buoi_cao', hairColor: '#d8d2cc', face: 'hien', skinTone: '#c8a080', outfit: 'ao_trang', accessory: 'chuoi_hat', aura: 'khong' }, gender: 'nam', variant: 'guide' },
  boatman: { appearance: { hairStyle: 'khan_vay', hairColor: '#3a2e28', face: 'nghiem', skinTone: '#9a6a48', outfit: 'ao_nau', accessory: 'khong', aura: 'suong_lam' }, gender: 'nam', variant: 'gatekeeper' },
  judge: { appearance: { hairStyle: 'buoi_cao', hairColor: '#1b1411', face: 'nghiem', skinTone: '#d0b090', outfit: 'ao_lam', accessory: 'tram_cai', aura: 'dom_vang' }, gender: 'nam', variant: 'envoy' },
};

/**
 * Điều phối thế giới: nạp/giải phóng scene theo tầng (không bao giờ giữ nhiều tầng cùng lúc),
 * đặt NPC, điểm tương tác, vật ẩn; cập nhật nhân vật + camera; tìm đối tượng tương tác gần nhất.
 */
export class WorldManager {
  readonly camera: ThirdPersonCamera;
  readonly input: InputController;
  readonly player: PlayerController;
  readonly remotes: RemotePlayers;
  /** Người soi đường cầm đèn dầu đi cùng người chơi */
  readonly lampBearer: LampBearer;
  /** Đèn hoa đăng đang trôi ở tầng hiện tại */
  readonly lanterns: RiverLanterns;
  /** Bé Lân – linh vật đi theo */
  readonly lionCub: LionCub;
  readonly fireworks: Fireworks;
  readonly skyLanterns: SkyLanterns;
  readonly lotusLights: LotusLights;
  private readonly aura: BuffAura;
  private buffUntil = 0;
  private readonly mascotIt: Extract<Interactable, { kind: 'mascot' }> = { kind: 'mascot', key: 'be_lan', name: 'Bé Lân', pos: new THREE.Vector3(), range: 2.2 };
  private soul: LostSoul | null = null;
  private incense: Incense[] = [];
  private playerLabel: THREE.Sprite;
  private readonly companionIt: Extract<Interactable, { kind: 'companion' }> = { kind: 'companion', key: 'lao_dang', name: 'Lão Đăng', pos: new THREE.Vector3(), range: 2.6 };
  private readonly soulIt: Extract<Interactable, { kind: 'soul' }> = { kind: 'soul', key: 'linh_hon', name: 'Linh hồn lạc lối', pos: new THREE.Vector3(), range: 2.8 };
  /** Lão Đăng có thể trò chuyện được (tắt trong lúc cinematic) */
  companionTalkable = true;
  /** Khoảng cách tới mảnh ký ức gần nhất (Infinity nếu không còn) */
  nearestMemory = Infinity;
  scene?: BuiltScene;
  private dynamic = new THREE.Group();
  private npcs = new Map<number, { info: NpcInfo; model: CharacterModel; talkUntil: number }>();
  private interactables: Interactable[] = [];
  private markers = new Map<string, THREE.Object3D>();
  private nearest: Interactable | null = null;
  private stepAcc = 0; private enhanceT = 0;
  onNearestChange?: (i: Interactable | null) => void;
  onStep?: () => void;
  frozen = false;
  /** Camera tự xoay theo hướng nhân vật (bật/tắt trong Cài đặt) */
  followCamera = true;
  private lastManualLook = 0;
  readonly courts = new Map<string, CourtSet>();
  private cine: { pos: THREE.Vector3; look: THREE.Vector3; snap: boolean } | null = null;
  private cineLook = new THREE.Vector3();
  conference: ReturnType<typeof buildConferenceHall> | null = null;

  constructor(readonly engine: Engine, appearance: Appearance, gender: Gender, playerName: string, onAction: (a: string) => void) {
    this.input = new InputController(engine.renderer.domElement, onAction);
    this.camera = new ThirdPersonCamera(engine.camera);
    const model = new CharacterModel({ appearance, gender });
    model.root.traverse((o) => { (o as THREE.Mesh).castShadow = engine.profile.shadows; });
    const label = nameSprite(playerName); label.position.y = 2.15; model.root.add(label); this.playerLabel = label;
    this.player = new PlayerController(model, () => 0, []);
    this.remotes = new RemotePlayers(engine.scene);
    engine.scene.add(model.root, this.dynamic);
    this.lampBearer = new LampBearer(engine.scene, engine.profile.shadows);
    this.lanterns = new RiverLanterns(engine.scene);
    this.lionCub = new LionCub(engine.scene, engine.profile.shadows);
    this.fireworks = new Fireworks(engine.scene); this.skyLanterns = new SkyLanterns(engine.scene); this.lotusLights = new LotusLights(engine.scene);
    this.aura = new BuffAura(model.root);
    engine.add(this.tick);
  }

  /** Nạp tầng mới: giải phóng toàn bộ tài nguyên tầng cũ trước. */
  load(data: SceneData, spawn: { x: number; z: number; rotY?: number }) {
    this.unload();
    const builder = SCENES[data.level.sceneKey];
    if (!builder) throw new Error(`Scene "${data.level.sceneKey}" chưa được xây dựng`);
    const sc = builder(this.engine.profile, data); this.scene = sc;
    this.engine.scene.add(sc.group); this.engine.scene.fog = sc.fog; this.engine.scene.background = sc.background;
    this.dynamic = new THREE.Group(); this.engine.scene.add(this.dynamic);
    enhance(sc.group); this.enhanceT = 0;
    { // v0.16: vũng sáng ấm dưới các đèn lồng treo thấp (giả ánh đèn hắt xuống đất – một InstancedMesh, gần như không tốn)
      sc.group.updateMatrixWorld(true); const pools: Array<[number, number, number]> = []; const wp = new THREE.Vector3(); const keyed = new Set<string>();
      sc.group.traverse((o) => { if (!o.userData.lantern) return; o.getWorldPosition(wp); const gy = sc.heightAt(wp.x, wp.z); if (wp.y - gy > 6 || wp.y - gy < 0.5) return; const k = `${Math.round(wp.x)}:${Math.round(wp.z)}`; if (keyed.has(k)) return; keyed.add(k); pools.push([wp.x, wp.z, gy]); });
      if (pools.length && !['villa', 'village', 'heaven'].includes(sc.key)) { const lp = lightPools(pools.slice(0, 400), '#ff9a50', 3.4); sc.group.add(lp); }
    }

    for (const n of data.npcs) {
      const look = NPC_LOOK[n.modelKey] ?? NPC_LOOK.guide;
      const model = new CharacterModel(look);
      model.root.traverse((o) => { (o as THREE.Mesh).castShadow = this.engine.profile.shadows; });
      model.root.position.set(n.x, sc.heightAt(n.x, n.z), n.z); model.root.rotation.y = n.rotY;
      const tag = nameSprite(n.name, n.title ?? undefined, '#e3be76'); tag.position.y = 2.35; model.root.add(tag);
      this.dynamic.add(model.root); this.npcs.set(n.id, { info: n, model, talkUntil: 0 });
      sc.colliders.push(circle(n.x, n.z, 0.55));
      this.interactables.push({ kind: 'npc', id: n.id, key: n.key, name: n.name, pos: new THREE.Vector3(n.x, 0, n.z), range: 3.2 });
    }
    for (const l of data.locations) {
      if (l.kind === 'spawn' || l.kind === 'poi') continue;
      const anchor = sc.anchors[l.key];
      const pos = new THREE.Vector3(l.x, sc.heightAt(l.x, l.z), l.z);
      this.interactables.push({ kind: 'location', key: l.key, name: l.label, pos, range: Math.max(2.6, l.radius), loc: l });
      this.addRuneMarker(l.key, pos, l.kind === 'exit' ? '#ff7a3a' : '#e3be76', anchor ? 1.2 : 1.6);
      if (l.kind === 'exit' && (data.level.sceneKey === 'festival' || data.level.sceneKey === 'village')) { // cổng về từ Phố Hội Đèn: thêm biển tên nổi (Tầng 00 đã có cổng dựng sẵn)
        const tag = nameSprite(l.label, undefined, '#ffd27a'); tag.position.set(pos.x, pos.y + 2.4, pos.z); this.dynamic.add(tag);
      }
    }
    for (const sp of sc.spots ?? []) this.interactables.push({ kind: 'venue', key: sp.key, name: sp.name, pos: new THREE.Vector3(sp.x, 0, sp.z), range: sp.range });
    for (const c of data.collectibles) {
      const y = sc.heightAt(c.x, c.z) + c.y;
      const orb = new THREE.Mesh(new THREE.OctahedronGeometry(0.22, 0), new THREE.MeshStandardMaterial({ color: '#ffe7a8', emissive: '#ffbf4a', emissiveIntensity: 2.2 }));
      orb.position.set(c.x, y, c.z); orb.userData.baseY = y; this.dynamic.add(orb); this.markers.set('col:' + c.key, orb);
      this.interactables.push({ kind: 'collectible', key: c.key, name: 'Mảnh ký ức', pos: new THREE.Vector3(c.x, y, c.z), range: 2.2 });
    }

    this.player.setWorld(sc.heightAt, sc.colliders);
    // v0.18: Khu Biệt Thự rộng ±100 m, công viên tới z -132 (tường bao của cảnh tự chặn) – cảnh khác giữ biên 78 m / đèn trôi 55 m như cũ
    const wide = sc.key === 'villa'; this.player.bound = wide ? 136 : 78; this.lanterns.bound = wide ? 136 : 55;
    this.camera.setWorld(sc.heightAt, sc.colliders, sc.occluders);
    this.camera.maxPitch = sc.camera.maxPitch; this.camera.maxDist = sc.camera.maxDist; this.camera.distance = Math.min(this.camera.distance, sc.camera.maxDist);
    this.player.teleport(spawn.x, spawn.z, spawn.rotY ?? Math.PI);
    this.lampBearer.setWorld(sc.heightAt, sc.colliders); this.lampBearer.setHidden(false); this.lampBearer.placeNear(this.player.position, this.player.rotY);
    this.lanterns.heightAt = sc.heightAt;
    this.lionCub.setWorld(sc.heightAt, sc.colliders); this.lionCub.setHidden(false); this.lionCub.placeNear(this.player.position, this.player.rotY);
    this.camera.yaw = (spawn.rotY ?? Math.PI) + Math.PI; this.camera.snap(this.player.position);
  }

  private addRuneMarker(key: string, pos: THREE.Vector3, color: string, r: number) {
    const ring = new THREE.Mesh(new THREE.RingGeometry(r * 0.75, r, 40), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    ring.rotation.x = -Math.PI / 2; ring.position.copy(pos).setY(pos.y + 0.04); this.dynamic.add(ring); this.markers.set(key, ring);
  }

  removeCollectible(key: string) {
    const m = this.markers.get('col:' + key); if (m) { disposeTree(m); this.markers.delete('col:' + key); }
    this.interactables = this.interactables.filter((i) => !(i.kind === 'collectible' && i.key === key));
  }

  setMarkerDone(key: string) { const m = this.markers.get(key) as THREE.Mesh | undefined; if (m) (m.material as THREE.MeshBasicMaterial).color.set('#6b8a6b'); }

  /** Đặt đại điện Thập Điện vào tầng hiện tại (colliders/occluders/điểm tương tác). */
  addCourt(info: YamaKingInfo, judgment: YamaJudgmentInfo | null) {
    if (!this.scene) return null;
    const court = new CourtSet(info, this.engine.profile, judgment);
    this.dynamic.add(court.group);
    court.group.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && this.engine.profile.shadows) m.castShadow = true; });
    this.scene.colliders.push(...court.colliders); this.camera.addOccluders(court.occluders);
    this.courts.set(info.key, court);
    const kp = new THREE.Vector3(info.court.x, 0, info.court.z);
    this.interactables.push({ kind: 'yama', key: `king:${info.key}`, name: `${info.name}`, pos: kp, range: 5.2, action: 'king', king: info.key });
    this.setCourtJudgment(info.key, judgment);
    return court;
  }
  /** Cập nhật chứng cứ / bàn án theo tiến độ phiên xét. */
  setCourtJudgment(kingKey: string, j: YamaJudgmentInfo | null) {
    const court = this.courts.get(kingKey); if (!court) return;
    court.setJudgment(j);
    this.interactables = this.interactables.filter((i) => !(i.kind === 'yama' && i.king === kingKey && i.action !== 'king'));
    if (!j || !j.evidence.length) return;
    for (const ev of j.evidence) this.interactables.push({ kind: 'yama', key: ev.key, name: ev.label, pos: new THREE.Vector3(ev.x, 0, ev.z), range: 2.6, action: 'evidence', king: kingKey });
    this.interactables.push({ kind: 'yama', key: `desk:${kingKey}`, name: 'Bàn án · Sổ Sinh Tử', pos: court.deskPos.clone(), range: 3.8, action: 'desk', king: kingKey });
  }

  /** Camera điện ảnh: đặt vị trí + điểm nhìn (lướt mượt); null ⇒ trả về camera thứ ba. */
  setCinematic(pos: THREE.Vector3 | null, look?: THREE.Vector3, snap = false) {
    if (!pos) { this.cine = null; this.camera.snap(this.player.position); return; }
    this.cine = { pos: pos.clone(), look: (look ?? this.player.position).clone(), snap };
    if (snap || !this.cineLook.lengthSq()) { this.engine.camera.position.copy(pos); this.cineLook.copy(this.cine.look); this.engine.camera.lookAt(this.cineLook); }
  }
  get cinematicOn() { return !!this.cine; }

  /** Cảnh đặc biệt Đại Điện Thập Phương (không phải một tầng). Gọi load() tầng thường để quay lại. */
  loadConference(kings: YamaKingInfo[]) {
    this.unload();
    const hall = buildConferenceHall(this.engine.profile, kings); this.conference = hall;
    this.engine.scene.add(hall.group); this.engine.scene.fog = hall.fog; this.engine.scene.background = hall.background;
    this.dynamic = new THREE.Group(); this.engine.scene.add(this.dynamic);
    this.player.setWorld(() => 0, []); this.player.teleport(0, 6, Math.PI);
    this.lampBearer.setWorld(() => 0, []); this.lampBearer.setHidden(true); this.lionCub.setHidden(true); // đại điện đã đủ sáng
    return hall;
  }

  unload() {
    this.clearGuide(); this.setQuestTargets([]);
    this.lanterns.clear(); this.fireworks.clear(); this.skyLanterns.clear(); this.lotusLights.clear(); this.removeSoul(); for (const i of this.incense) i.dispose(); this.incense = [];
    for (const c of this.courts.values()) c.dispose(); this.courts.clear();
    if (this.conference) { this.conference.dispose(); disposeTree(this.conference.group); this.conference = null; }
    this.cine = null;
    for (const n of this.npcs.values()) n.model.dispose();
    this.npcs.clear(); this.interactables = []; this.markers.clear(); this.nearest = null;
    disposeTree(this.dynamic);
    if (this.scene) { disposeTree(this.scene.group); this.scene = undefined; }
    this.onNearestChange?.(null);
  }

  npcTalk(id: number, sec = 4) {
    const n = this.npcs.get(id); if (!n) return;
    n.talkUntil = performance.now() + sec * 1000; n.model.setAnim('talk');
    n.model.root.rotation.y = Math.atan2(this.player.position.x - n.info.x, this.player.position.z - n.info.z);
    this.focus(n.info.x, n.info.z, sec > 1);
  }

  /** Khung hình hội thoại / xem vật thể: người chơi quay về mục tiêu, camera qua vai. focus(…, false) trả camera về như cũ. */
  focus(x: number, z: number, on: boolean, dist = 4.2) {
    if (on) {
      this.player.faceTowards(x, z);
      this.savedCam ??= { yaw: this.camera.yaw, dist: this.camera.distance, pitch: this.camera.pitch };
      this.camera.yaw = this.player.rotY + Math.PI + 0.55; this.camera.distance = dist; this.camera.pitch = 0.18;
    } else if (this.savedCam) { this.camera.yaw = this.savedCam.yaw; this.camera.distance = this.savedCam.dist; this.camera.pitch = this.savedCam.pitch; this.savedCam = null; }
  }
  private savedCam: { yaw: number; dist: number; pitch: number } | null = null;
  npcGesture(id: number, a: 'bow' | 'point' | 'wave') { this.npcs.get(id)?.model.setAnim(a); }

  private tick = (dt: number, t: number) => {
    if (this.conference) {
      this.conference.update(dt, t); this.player.model.update(dt); this.lampBearer.update(dt, this.player);
      if (this.cine) this.applyCine(dt);
      return;
    }
    if (!this.scene) return;
    const look = this.input.consumeLook();
    if (!this.frozen) {
      this.camera.rotate(look.dx, look.dy);
      // lăn chuột: ở góc nhìn thứ nhất lăn ra ⇒ về góc thứ ba; ở góc thứ ba zoom sát hết cỡ rồi lăn tiếp ⇒ góc thứ nhất
      if (this.camera.firstPerson) { if (look.zoom > 0) this.setFirstPerson(false); }
      else if (look.zoom < 0 && this.camera.distance <= this.camera.minDist + 0.01) { this.zoomInT += -look.zoom; if (this.zoomInT > 1.5) this.setFirstPerson(true); }
      else if (look.zoom) { this.zoomInT = 0; this.camera.zoom(look.zoom); }
    }
    const mv = this.frozen ? { x: 0, y: 0, run: false } : this.input.moveVector();
    this.player.update(dt, mv, this.camera.forward());
    // Camera tự xoay về sau lưng nhân vật khi đang di chuyển (trừ khi vừa xoay tay hoặc đang đi lùi)
    const now0 = performance.now();
    if (look.dx || look.dy) this.lastManualLook = now0;
    const backward = mv.y < -0.3 && Math.abs(mv.y) >= Math.abs(mv.x);
    if (this.followCamera && !this.camera.firstPerson && !this.frozen && this.player.speed > 0.5 && !backward && now0 - this.lastManualLook > 1500) {
      let d = this.player.rotY + Math.PI - this.camera.yaw; d = Math.atan2(Math.sin(d), Math.cos(d));
      const rate = (this.player.speed > 5 ? 3 : 2) * Math.min(1, Math.abs(d) * 2 + 0.3); // xoay êm, nhanh dần khi lệch nhiều
      this.camera.yaw += d * Math.min(1, dt * rate);
    }
    if (this.camera.firstPerson && !this.cine && this.player.pose !== 'sit') { this.player.rotY = this.camera.yaw + Math.PI; this.player.model.root.rotation.y = this.player.rotY; }
    this.player.model.root.visible = !(this.camera.firstPerson && !this.cine);
    this.player.model.update(dt);
    if (this.cine) this.applyCine(dt); else this.camera.update(this.player.position, dt);
    this.scene.update(dt, t, this.player.position);
    for (const c of this.courts.values()) c.update(dt, t);
    const now = performance.now();
    for (const n of this.npcs.values()) { if (n.talkUntil && now > n.talkUntil) { n.talkUntil = 0; n.model.setAnim('idle'); } n.model.update(dt); }
    for (const [k, m] of this.markers) {
      if (k.startsWith('col:')) { m.rotation.y += dt * 1.6; m.position.y = (m.userData.baseY as number) + Math.sin(t * 2) * 0.15; }
      else { m.rotation.z += dt * 0.4; ((m as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.4 + Math.sin(t * 2.4) * 0.15; }
    }
    // Lão Đăng "cảm" mảnh ký ức gần nhất (< 12 m)
    let md = Infinity; for (const [k, m] of this.markers) if (k.startsWith('col:')) md = Math.min(md, Math.hypot(m.position.x - this.player.position.x, m.position.z - this.player.position.z));
    this.nearestMemory = md; this.lampBearer.sense = md < 12 ? Math.min(1, Math.max(0, 1 - (md - 2) / 10)) : 0;
    this.lampBearer.update(dt, this.player); this.updateGuide(dt); this.updateQuestMk(t);
    animateWater(t); (this.glowItem?.userData.spin as ((t: number) => void) | undefined)?.(t); this.enhanceT -= dt; if (this.enhanceT <= 0 && this.scene) { this.enhanceT = 2; enhance(this.scene.group); } // nhà / biệt thự dựng động cũng được nâng vật liệu
    this.lanterns.update(dt, t, this.player.position);
    this.lionCub.followDance = !!this.player.pose && this.player.pose.startsWith('dance');
    this.lionCub.update(dt, this.player);
    this.fireworks.update(dt); this.skyLanterns.update(dt, t); this.lotusLights.update(dt, t);
    if (this.buffUntil && now > this.buffUntil) { this.buffUntil = 0; this.player.speedMul = 1; this.aura.set(null); }
    this.aura.update(dt);
    if (this.soul && !this.soul.update(dt, this.player.position)) this.removeSoul();
    this.incense = this.incense.filter((i) => { const alive = i.update(dt); if (!alive) i.dispose(); return alive; });
    this.remotes.update(dt, this.engine.camera.position);
    // bước chân
    if (this.player.speed > 0.5) { this.stepAcc += dt * (this.player.speed > 5 ? 3.4 : 2.2); if (this.stepAcc > 1) { this.stepAcc = 0; this.onStep?.(); } }
    // đối tượng tương tác gần nhất
    let best: Interactable | null = null; let bd = Infinity;
    const dyn: Interactable[] = [];
    if (this.soul && !this.soul.done) { this.soulIt.pos.copy(this.soul.position); dyn.push(this.soulIt); }
    for (const i of [...this.interactables, ...dyn]) { const d = Math.hypot(i.pos.x - this.player.position.x, i.pos.z - this.player.position.z); if (d < i.range && d < bd) { bd = d; best = i; } }
    // Lão Đăng & Bé Lân luôn đi cạnh ⇒ chỉ chọn khi không có NPC/vật thể nào khác trong tầm (không "giành" phím E);
    // cả hai cùng trong tầm ⇒ chọn bạn nào nằm phía trước mặt người chơi hơn (quay về phía ai thì nói chuyện với người đó)
    if (!best) {
      const fx = Math.sin(this.player.rotY), fz = Math.cos(this.player.rotY); let bestDot = -Infinity;
      const consider = (it: Interactable, p: THREE.Vector3) => {
        it.pos.copy(p); const dx = p.x - this.player.position.x, dz = p.z - this.player.position.z; const d = Math.hypot(dx, dz);
        if (d >= it.range) return; const dot = d > 1e-3 ? (dx * fx + dz * fz) / d : 1; if (dot > bestDot) { bestDot = dot; best = it; }
      };
      if (this.companionTalkable && this.lampBearer.visible) consider(this.companionIt, this.lampBearer.position);
      if (this.lionCub.visible) consider(this.mascotIt, this.lionCub.position);
    }
    if (best !== this.nearest) { this.nearest = best; this.onNearestChange?.(best); }
  };

  private applyCine(dt: number) {
    const k = this.cine!.snap ? 1 : 1 - Math.exp(-dt * 2.2);
    this.engine.camera.position.lerp(this.cine!.pos, k); this.cineLook.lerp(this.cine!.look, k); this.engine.camera.lookAt(this.cineLook);
  }

  getNearest() { return this.nearest; }
  private zoomInT = 0;
  /** Đổi góc nhìn thứ nhất / thứ ba (phím F5 / P, lăn chuột). */
  onViewChange?: (firstPerson: boolean) => void;
  setFirstPerson(on: boolean) {
    if (this.camera.firstPerson === on) return; this.zoomInT = 0;
    this.camera.setFirstPerson(on); if (!on) { this.camera.distance = Math.max(this.camera.minDist + 1, 4.5); this.camera.snap(this.player.position); }
    this.onViewChange?.(on);
  }
  get firstPerson() { return this.camera.firstPerson; }

  /** Ngồi vào ghế (rạp phim): đặt nhân vật, quay về màn hình, tư thế ngồi. */
  sitAt(seat: { x: number; z: number; rotY: number }) {
    this.player.teleport(seat.x, seat.z, seat.rotY); this.player.pose = 'sit';
  }
  /** Chế độ xem phim: ẩn nhãn tên của mình và Lão Đăng cho khỏi che màn hình. */
  setWatching(on: boolean) { this.playerLabel.visible = !on; this.lampBearer.setHidden(on); this.lionCub.setHidden(on); }
  setPose(p: 'sit' | DanceKey | null) { if (p) this.player.autoTarget = null; this.player.pose = p; }

  /** Đổi ngoại hình người chơi: dựng lại mô hình, giữ vị trí + nhãn tên. */
  setAppearance(appearance: Appearance, gender: Gender) {
    const old = this.player.model; const model = new CharacterModel({ appearance, gender });
    model.root.traverse((o) => { (o as THREE.Mesh).castShadow = this.engine.profile.shadows; });
    model.root.add(this.playerLabel); // chuyển nhãn tên sang mô hình mới
    model.root.position.copy(old.root.position); model.root.rotation.y = old.root.rotation.y;
    old.dispose(); this.engine.scene.add(model.root); this.player.model = model; this.aura.reparent(model.root);
  }

  /** Thắp hương: lư hương nhỏ trước mặt người chơi, khói bay ~30 giây. */
  burnIncense() {
    const f = new THREE.Vector3(Math.sin(this.player.rotY), 0, Math.cos(this.player.rotY));
    const p = this.player.position.clone().addScaledVector(f, 1.1); p.y = this.scene?.heightAt(p.x, p.z) ?? p.y;
    this.incense.push(new Incense(this.dynamic, p));
  }

  /** Linh hồn lạc lối cho việc mỗi ngày (đặt ở điểm đi được do client chọn). */
  spawnSoul(x: number, z: number) { this.removeSoul(); this.soul = new LostSoul(this.dynamic, x, this.scene?.heightAt(x, z) ?? 0, z); }
  releaseSoul() { this.soul?.release(); }
  /** v0.17 Độ hồn lạc: đốm sáng đánh dấu món đồ hồn lạc đánh rơi (null = ẩn) */
  private glowItem: THREE.Group | null = null;
  setGlowItem(p: { x: number; z: number; label?: string } | null) {
    if (this.glowItem) { this.glowItem.parent?.remove(this.glowItem); this.glowItem.traverse((o) => { const mm = o as THREE.Mesh; mm.geometry?.dispose(); (mm.material as THREE.Material | undefined)?.dispose?.(); }); this.glowItem = null; }
    if (!p) return;
    const g = new THREE.Group(); const y = this.scene?.heightAt(p.x, p.z) ?? 0; g.position.set(p.x, y, p.z);
    const orb = new THREE.Mesh(new THREE.OctahedronGeometry(0.22), new THREE.MeshStandardMaterial({ color: '#bfe8ff', emissive: '#6fc8ff', emissiveIntensity: 2.2 })); orb.position.y = 0.7; g.add(orb);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTexture(), color: '#8fd8ff', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })); halo.scale.set(1.8, 1.8, 1); halo.position.y = 0.7; g.add(halo);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.5, 0.62, 32), new THREE.MeshBasicMaterial({ color: '#8fd8ff', transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false })); ring.rotation.x = -Math.PI / 2; ring.position.y = 0.05; g.add(ring);
    if (p.label) { const tag = nameSprite(p.label, undefined, '#bfe8ff'); tag.position.y = 1.5; tag.scale.multiplyScalar(0.7); g.add(tag); }
    g.userData.spin = (t: number) => { orb.rotation.y = t * 2; orb.position.y = 0.7 + Math.sin(t * 2.4) * 0.12; (ring.material as THREE.MeshBasicMaterial).opacity = 0.35 + Math.sin(t * 3) * 0.25; };
    this.engine.scene.add(g); this.glowItem = g;
  }
  get hasSoul() { return !!this.soul && !this.soul.done; }
  getSoulPos() { return this.soul ? { x: this.soul.position.x, z: this.soul.position.z } : null; }
  private removeSoul() { if (!this.soul) return; this.soul.dispose(); this.soul = null; if (this.nearest?.kind === 'soul') { this.nearest = null; this.onNearestChange?.(null); } }
  /** Điểm trống (không va chạm) gần (x,z) – để đặt linh hồn */
  freeSpotNear(x: number, z: number, tries = 24): { x: number; z: number } | null {
    if (!this.scene) return null;
    for (let i = 0; i < tries; i++) {
      const a = Math.random() * Math.PI * 2; const r = 3 + Math.random() * 5; const px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
      if (Math.abs(px) > 50 || Math.abs(pz) > 50) continue;
      const res = resolveCircle(px, pz, 0.6, this.scene.colliders);
      if (Math.hypot(res.x - px, res.z - pz) >= 0.01) continue;
      // phải đi tới được từ điểm gốc: dọc đường thẳng không xuyên tường/vách (tránh đặt ngoài rìa thế giới)
      let ok = true; const n = Math.ceil(r / 0.5);
      for (let k = 2; k < n && ok; k++) { const u = k / n; const sx = x + (px - x) * u, sz = z + (pz - z) * u; const q = resolveCircle(sx, sz, 0.3, this.scene.colliders); if (Math.hypot(q.x - sx, q.z - sz) > 0.01) ok = false; }
      if (ok) return { x: px, z: pz };
    }
    return null;
  }
  /** Hiệu ứng món ăn: speed (chạy nhanh hơn chút), warm/sparkle/cloud/refresh (hạt bay quanh người). */
  applyBuff(effect: string, sec: number) {
    this.player.speedMul = effect === 'speed' ? 1.2 : 1; this.buffUntil = performance.now() + sec * 1000; this.aura.set(effect);
    this.player.model.setAnim('cheer', 1.2);
  }
  get buffLeft() { return this.buffUntil ? Math.max(0, (this.buffUntil - performance.now()) / 1000) : 0; }
  groundAt(x: number, z: number) { return this.scene?.heightAt(x, z) ?? 0; }
  /** Điểm tương tác động (vd góc thờ từng nhà, lô đất trống ở Làng Nhà Riêng): thay toàn bộ điểm có key bắt đầu bằng prefix. */
  setSpots(prefix: string, spots: Array<{ key: string; name: string; x: number; z: number; range: number }>) {
    this.interactables = this.interactables.filter((i) => !(i.kind === 'venue' && i.key.startsWith(prefix)));
    for (const sp of spots) this.interactables.push({ kind: 'venue', key: sp.key, name: sp.name, pos: new THREE.Vector3(sp.x, 0, sp.z), range: sp.range });
    if (this.nearest?.kind === 'venue' && this.nearest.key.startsWith(prefix)) { this.nearest = null; this.onNearestChange?.(null); }
  }
  setAutoMove(x: number, z: number) { this.clearGuide(); this.player.autoTarget = new THREE.Vector3(x, 0, z); }

  // ───────── dẫn đường theo lộ trình (v0.10: "về nhà", "ghé nhà ai đó" ở Làng Nhà Riêng) ─────────
  private guidePath: THREE.Vector3[] = []; private guideFx: THREE.Group | null = null; private guideT = 0;
  /** Vẽ vệt đèn dẫn đường trên mặt đất qua các điểm và (nếu walk) cho nhân vật tự đi theo. Người chơi tự bấm di chuyển thì nhân vật dừng tự đi nhưng vệt vẫn còn tới khi tới nơi. */
  guideTo(points: Array<{ x: number; z: number }>, walk = true) {
    this.clearGuide(); if (!points.length) return;
    const p0 = this.player.position; const pts = [new THREE.Vector3(p0.x, 0, p0.z), ...points.map((p) => new THREE.Vector3(p.x, 0, p.z))];
    const g = new THREE.Group(); const tex = this.guideTexture();
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i]; const len = a.distanceTo(b); const n = Math.max(1, Math.floor(len / 1.6));
      for (let k = 0; k < n; k++) {
        const u = (k + 0.5) / n; const x = a.x + (b.x - a.x) * u, z = a.z + (b.z - a.z) * u;
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: '#ffd27a', transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }));
        sp.scale.set(0.55, 0.55, 1); sp.position.set(x, this.groundAt(x, z) + 0.25, z); sp.userData.k = g.children.length; g.add(sp);
      }
    }
    const last = pts[pts.length - 1]; const beacon = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: '#fff0b0', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    beacon.scale.set(1.6, 4.5, 1); beacon.position.set(last.x, this.groundAt(last.x, last.z) + 2.2, last.z); beacon.userData.beacon = true; g.add(beacon);
    this.engine.scene.add(g); this.guideFx = g; this.guidePath = walk ? pts.slice(1) : []; this.guideEnd = last;
    if (walk) this.player.autoTarget = this.guidePath[0].clone();
  }
  private guideEnd: THREE.Vector3 | null = null; private guideTex?: THREE.Texture;
  private guideTexture() {
    if (this.guideTex) return this.guideTex;
    const c = document.createElement('canvas'); c.width = c.height = 64; const x = c.getContext('2d')!; const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.35, 'rgba(255,220,140,0.8)'); gr.addColorStop(1, 'rgba(255,180,80,0)'); x.fillStyle = gr; x.fillRect(0, 0, 64, 64);
    return (this.guideTex = new THREE.CanvasTexture(c));
  }
  clearGuide() {
    if (this.guideFx) { this.guideFx.removeFromParent(); this.guideFx.traverse((o) => (o as THREE.Sprite).material?.dispose?.()); }
    this.guideFx = null; this.guidePath = []; this.guideEnd = null;
  }
  get guiding() { return !!this.guideFx; }

  /* ───── v0.13: chỉ dẫn trực tiếp – mốc nhiệm vụ luôn hiện (cột sáng + hình thoi nhấp nhô) ───── */
  private questMk: THREE.Group | null = null; private questPts: Array<{ x: number; z: number; name: string }> = [];
  /** Đặt các điểm mục tiêu của nhiệm vụ đang theo dõi trong tầng hiện tại (rỗng ⇒ ẩn). Mốc tự chọn điểm gần nhất. */
  setQuestTargets(points: Array<{ x: number; z: number; name: string }>) {
    this.questPts = points;
    if (!points.length) { if (this.questMk) this.questMk.visible = false; return; }
    if (!this.questMk) {
      const g = new THREE.Group(); const tex = this.guideTexture();
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.6, 14, 12, 1, true), new THREE.MeshBasicMaterial({ color: '#ffcf6a', transparent: true, opacity: 0.16, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
      beam.position.y = 7; g.add(beam);
      const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: '#ffe3a0', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })); glow.scale.set(2.2, 2.2, 1); glow.position.y = 0.3; g.add(glow);
      const dia = new THREE.Mesh(new THREE.OctahedronGeometry(0.42), new THREE.MeshBasicMaterial({ color: '#ffd27a', transparent: true, opacity: 0.95 })); dia.scale.y = 1.5; dia.name = 'dia'; g.add(dia);
      g.renderOrder = 5; this.engine.scene.add(g); this.questMk = g;
    }
    this.questMk.visible = true; this.placeQuestMk();
  }
  /** Điểm mục tiêu gần nhất + khoảng cách, để giao diện vẽ la bàn. */
  questTarget() {
    if (!this.questPts.length) return null; const p = this.player.position;
    let best = this.questPts[0], bd = Infinity; for (const q of this.questPts) { const d = Math.hypot(q.x - p.x, q.z - p.z); if (d < bd) { bd = d; best = q; } }
    /* góc so với hướng camera (0 = thẳng phía trước màn hình, dương = bên phải) */
    const cam = this.engine.camera; const fw = new THREE.Vector3(); cam.getWorldDirection(fw);
    const a = Math.atan2(best.x - p.x, best.z - p.z) - Math.atan2(fw.x, fw.z);
    return { ...best, dist: bd, angle: -Math.atan2(Math.sin(a), Math.cos(a)) };
  }
  private placeQuestMk() { const t = this.questTarget(); if (t && this.questMk) this.questMk.position.set(t.x, this.groundAt(t.x, t.z), t.z); }
  private updateQuestMk(t: number) {
    if (!this.questMk?.visible) return; this.placeQuestMk();
    const d = this.questMk.getObjectByName('dia'); if (d) { d.position.y = 2.6 + Math.sin(t * 2.4) * 0.25; d.rotation.y = t * 1.6; }
    const near = (this.questTarget()?.dist ?? 99) < 3; (this.questMk.children[0] as THREE.Mesh).visible = !near; /* tới nơi ⇒ tắt cột sáng cho khỏi chói */
  }
  private updateGuide(dt: number) {
    if (!this.guideFx) return; this.guideT += dt; const p = this.player.position;
    for (const o of this.guideFx.children) { const m = (o as THREE.Sprite).material as THREE.SpriteMaterial; m.opacity = o.userData.beacon ? 0.55 + Math.sin(this.guideT * 3) * 0.2 : 0.35 + 0.5 * Math.max(0, Math.sin(this.guideT * 4 - (o.userData.k as number) * 0.5)); }
    if (this.guidePath.length && !this.player.autoTarget) { // tới điểm ⇒ đi điểm kế; người chơi tự điều khiển ⇒ thôi tự đi
      const cur = this.guidePath[0]; if (Math.hypot(cur.x - p.x, cur.z - p.z) < 2.6) { this.guidePath.shift(); if (this.guidePath.length) this.player.autoTarget = this.guidePath[0].clone(); } else this.guidePath = [];
    }
    if (this.guideEnd && Math.hypot(this.guideEnd.x - p.x, this.guideEnd.z - p.z) < 2.2) this.clearGuide();
  }

  dispose() { this.unload(); this.remotes.dispose(); this.input.dispose(); this.player.model.dispose(); this.lampBearer.dispose(); this.lanterns.dispose(); this.lionCub.dispose(); this.fireworks.dispose(); this.skyLanterns.dispose(); this.lotusLights.dispose(); this.aura.dispose(); }
}
